import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { ILocalizedInformation, ITheme } from '@inter-ikea-kompis/types';
import { DexfSettingsUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { FocusRing, Hyperlink } from '@ingka/common-styles-webc';
import { laptopCheckmark, registerIcon, speechBubble } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';

import type IAssistanceCardDexfSettings from '../../types/IAssistanceCardDexfSettings.js';
import type IAssistanceCardTranslations from '../../types/IAssistanceCardTranslations.js';
import AssistanceCardStyle from './AssistanceCardStyle.js';

/**
 * The Assistance Card is a supporting component mainly intended to be used in
 * the Summary Page. It helps the user in the customer journey to validate and
 * get confident in the purchase by enabling them to contact co-workers when
 * shopping from home or in store.
 *
 * @event linkClick Triggered when the link is clicked.
 */

export default class AssistanceCard extends KompisElement {
	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	// Localized information. Deprecated - use "translations" and "settings" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IAssistanceCardTranslations | null = null;

	// The settings retrieved from settings service.
	@property({ type: Object })
	public settings: IAssistanceCardDexfSettings | null = null;

	// The VPC code for the saved design.
	@property({ type: String })
	public vpcCode: string | null = null;

	// The name of the application, e.g. "pax". Used in the booking url.
	@property({ type: String })
	public applicationName: string | null = null;

	// Disables links and presents a different message. This attribute should be set to "true" when in a Kiosk.
	@property({ type: Boolean })
	public disableLinks = false;

	private dexfSettings: IAssistanceCardDexfSettings | null = null;

	public override async connectedCallback(): Promise<void> {
		registerIcon(laptopCheckmark);
		registerIcon(speechBubble);

		this.customElements.define('skapa-icon', SkapaIcon);

		super.connectedCallback();
	}

	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslationsAndSettings()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}
		return html`
			<style>
				${Hyperlink.CSS + FocusRing.CSS}
				${AssistanceCardStyle.getStyle({
					theme: this.theme
				})}
			</style>
			${this.disableLinks ? this.getKioskVersion() : this.getStandardVersion()}
		`;
	}

	protected override shouldUpdate(
		changedProperties: Map<string, object | number | string | boolean>
	): boolean {
		if (
			changedProperties.has('localizedInformation') &&
			this.localizedInformation &&
			!this.settings
		) {
			this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
		}
		if (changedProperties.has('settings') && this.settings) {
			this.dexfSettings = this.settings;
		}

		return true;
	}

	private getSettings(): IAssistanceCardDexfSettings {
		return <IAssistanceCardDexfSettings>this.dexfSettings;
	}

	private hasTranslationsAndSettings(): boolean {
		return !!this.localizedInformation || !!(this.translations && this.settings);
	}

	private getTranslations(): IAssistanceCardTranslations {
		const translations = this.translations
			? this.translations
			: this.localizedInformation?.translations;

		return <IAssistanceCardTranslations>translations;
	}

	private getKioskVersion(): RenderResult {
		return html`
			<skapa-icon icon="speech-bubble"></skapa-icon>
			<div class="text">
				<h3>${this.getTranslations().assistanceCardKioskHeader}</h3>
				<p class="body">${this.getTranslations().assistanceCardKioskBody}</p>
				${this.vpcCode &&
				html`
					<p class="hint">${this.getTranslations().designCodeTitle} <b>${this.vpcCode}</b></p>
				`}
			</div>
		`;
	}

	private getStandardVersion(): RenderResult {
		return html`
			<skapa-icon icon="laptop-checkmark"></skapa-icon>
			<div class="text">
				<h3>${this.getTranslations().assistanceCardWebHeader}</h3>
				<p class="body">${this.getTranslations().assistanceCardWebBody}</p>
				${this.getLink()}
				${this.vpcCode &&
				html`
					<p class="hint">
						${this.getTranslations().assistanceCardWebCodeHint}
						<b>${this.vpcCode}</b>
					</p>
				`}
			</div>
		`;
	}

	/**
	 * Renders a link using the old Kompis hyperlink clone.
	 * Should be replaced by styling from Skapa in the future (@ingka/focus and @ingka/hyperlink-shared).
	 */
	private getLink(): RenderResult {
		const url = this.constructUrl();
		if (!url) {
			return null;
		}

		return html`
			<div class="link-wrapper">
				<a
					class="${Hyperlink.classnames()}"
					href="${url}"
					target="_blank"
					@click="${() => {
						this.dispatchEvent(new CustomEvent('linkClick', { bubbles: true }));
					}}"
					>${this.getTranslations().assistanceCardWebLink}</a
				>
			</div>
		`;
	}

	private constructUrl(): string | null {
		if (!this.applicationName) {
			return null;
		}
		return this.getSettings()
			.kompis.urls.supportBookingLink.replace('{applicationName}', this.applicationName)
			.replace('{vpcCode}', this.vpcCode ?? '');
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-icon': SkapaIcon;
		'kompis-assistance-card': AssistanceCard;
	}
}
